import React from 'react';

interface ActivityEvent {
  timestamp: string;
  message: string;
  logStream: string;
}

interface RecentActivityCardProps {
  events: ActivityEvent[];
  loading?: boolean;
  error?: string | null;
}

function RecentActivityCard({ events, loading, error }: RecentActivityCardProps) {
  // Determine event type from message
  const getEventType = (message: string): 'task' | 'deploy' | 'error' | 'info' => {
    const lowerMsg = message.toLowerCase();
    if (lowerMsg.includes('error') || lowerMsg.includes('fail')) return 'error';
    if (lowerMsg.includes('deploy') || lowerMsg.includes('push') || lowerMsg.includes('commit')) return 'deploy';
    if (lowerMsg.includes('task') || lowerMsg.includes('complete')) return 'task';
    return 'info';
  };

  const typeIcons = {
    task: '✅',
    deploy: '🚀',
    error: '❌',
    info: '💬',
  };

  // Parse agent name from log stream or message
  const getAgentName = (event: ActivityEvent): string => {
    if (event.logStream?.includes('moltbot')) return 'Moltbot';
    if (event.logStream?.includes('architect')) return 'Architect';
    if (event.message?.includes('moltbot')) return 'Moltbot';
    if (event.message?.includes('architect')) return 'Architect';
    return 'System';
  };

  // Format timestamp to relative time
  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex items-start gap-3 pb-4 border-b border-mc-border last:border-0">
              <div className="animate-pulse bg-mc-border h-6 w-6 rounded"></div>
              <div className="flex-1">
                <div className="animate-pulse bg-mc-border h-4 w-full rounded mb-2"></div>
                <div className="animate-pulse bg-mc-border h-3 w-20 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
        <p className="text-mc-warning text-sm">{error}</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
        <p className="text-mc-muted text-sm">No recent activity</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>

      <div className="space-y-4">
        {events.slice(0, 10).map((event, index) => {
          const eventType = getEventType(event.message);
          const agentName = getAgentName(event);

          return (
            <div
              key={`${event.timestamp}-${index}`}
              className="flex items-start gap-3 pb-4 border-b border-mc-border last:border-0 last:pb-0"
            >
              <span className="text-lg">{typeIcons[eventType]}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  <span className="font-semibold text-mc-accent">{agentName}</span>
                  {' '}
                  <span className="text-mc-text truncate">{event.message.substring(0, 100)}</span>
                </p>
                <p className="text-xs text-mc-muted mt-1">{formatTime(event.timestamp)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RecentActivityCard;
